import { z } from 'zod'
import { compareTime } from 'shared/service/helpers/DateUtilities'
import LSDRadioButtonValue from '../enums/LSDRadioButtonValue'
import EDDRadioButtonValue from '../enums/EDDRadioButtonValue'
import LDDRadioButtonValue from '../enums/LDDRadioButtonValue'

export const createKlantGroepSchema = (
  lsdRadioButtonValue: LSDRadioButtonValue,
  eddRadioButtonValue: EDDRadioButtonValue,
  lddRadioButtonValue: LDDRadioButtonValue,
  ordertijdIndicatie: string,
  verrekenIndicatie: string,
  uitersteInkoopTijdstipIndicatie: boolean
) => {
  return z
    .object({
      klantGroepCode: z
        .number({ error: 'De tijdvakcode moet groter dan 0 zijn' })
        .nonnegative()
        .int(),
      klantGroepOmschrijving: z
        .string()
        .nonempty('Vul a.u.b. een omschrijving in.'),
      orderType: z.string(),
      dagen: z.string(),
      productType: z.string(),
      vrijgaveTijdstip:
        ordertijdIndicatie === 'N'
          ? z.string().nonempty('Vul a.u.b. een tijdstip in.')
          : z.any(),
      ordertijdIndicatie: z.string<'J' | 'N'>(),
      shipmentDaysBeforeDelivery: z.coerce
        .number('Vul a.u.b. een aantal dagen in.')
        .min(0, 'Het aantal dagen moet tussen 0 en 9 liggen.')
        .max(9, 'Het aantal dagen moet tussen 0 en 9 liggen.')
        .int('Het aantal dagen moet een heel getal zijn.'),
      aflevertijd:
        lsdRadioButtonValue === LSDRadioButtonValue.FIXED_TIME
          ? z.string().nonempty('Vul a.u.b. een tijdstip in.')
          : z.any(),
      doorlooptijdInMinuten:
        lsdRadioButtonValue === LSDRadioButtonValue.LEAD_TIME_AFTER_ESD
          ? z.coerce
              .number('Vul a.u.b. een doorlooptijd in.')
              .nonnegative('De doorlooptijd moet groter of gelijk zijn aan 0.')
          : z.any(),
      lsdLeadTimeBeforeLDD:
        lsdRadioButtonValue === LSDRadioButtonValue.LEAD_TIME_BEFORE_LDD
          ? z.coerce
              .number('Vul a.u.b. een doorlooptijd in.')
              .nonnegative(
                'De waarde moet groter zijn dan of gelijk zijn aan 0.'
              )
          : z.any(),
      edd:
        eddRadioButtonValue === EDDRadioButtonValue.FIXED_TIME
          ? z.string().nonempty('Vul a.u.b. een tijdstip in.')
          : z.any(),
      eddWorkDaysAfterLSD:
        eddRadioButtonValue === EDDRadioButtonValue.FIXED_TIME
          ? z.coerce
              .number('Vul a.u.b. een aantal werkdagen in.')
              .nonnegative('Het aantal werkdagen na LSD moet 0 of groter zijn.')
              .int()
          : z.any(),
      eddLeadTimeAfterESD:
        eddRadioButtonValue === EDDRadioButtonValue.LEAD_TIME_AFTER_ESD
          ? z.coerce
              .number('Vul a.u.b. een doorlooptijd in.')
              .nonnegative(
                'De waarde moet groter zijn dan of gelijk zijn aan 0.'
              )
          : z.any(),
      ldd:
        lddRadioButtonValue === LDDRadioButtonValue.FIXED_TIME
          ? z.string().nonempty('Vul a.u.b. een tijdstip in.')
          : z.any(),
      lddLeadTimeAfterEDD:
        lddRadioButtonValue === LDDRadioButtonValue.LEAD_TIME_AFTER_EDD
          ? z.coerce
              .number('Vul a.u.b. een doorlooptijd in.')
              .nonnegative(
                'De waarde moet groter zijn dan of gelijk zijn aan 0.'
              )
          : z.any(),
      lddLeadTimeAfterLSD:
        lddRadioButtonValue === LDDRadioButtonValue.LEAD_TIME_AFTER_LSD
          ? z.coerce
              .number('Vul a.u.b. een doorlooptijd in.')
              .nonnegative(
                'De waarde moet groter zijn dan of gelijk zijn aan 0.'
              )
          : z.any(),
      uitersteInkoopTijdstipIndicatie: z.boolean(),
      uitersteInkoopTijdstip: uitersteInkoopTijdstipIndicatie
        ? z
            .string()
            .nonempty(
              'Vul a.u.b. bij Final Purchasing Time het tijdstip en het overloop tijdvak in.'
            )
        : z.any(),
      overflowFPTId: uitersteInkoopTijdstipIndicatie
        ? z.coerce
            .number(
              'Vul a.u.b. bij Final Purchasing Time het tijdstip en het overloop tijdvak in.'
            )
            .nonnegative()
        : z.any(),
      pilotIndicatie: z.boolean(),
      tijdvakBeginTijdstip: z.string(),
      tijdvakEindTijdstip: z.string(),
      tijdvakKosten: z.coerce
        .number({
          error: 'Tijdvakkosten moet een getal zijn.',
        })
        .max(9999, 'Tijdvakkosten mogen niet hoger zijn dan 9999.'),
      vasCode:
        verrekenIndicatie === 'J'
          ? z.object({
              code: z.string(),
              config: z.object({
                DeliveryPeriodStartTime: z.string(),
                DeliveryPeriodEndTime: z.string(),
                MarginInMinutesBefore: z.coerce
                  .number({ error: `Marge 'Voor' mag niet negatief zijn.` })
                  .nonnegative(),
                MarginInMinutesAfter: z.coerce
                  .number({ error: `Marge 'Na' mag niet negatief zijn.` })
                  .nonnegative(),
              }),
            })
          : z.any(),
      verrekenIndicatie: z.string<'J' | 'N'>(),
      defaultIndicatie: z.string<'J' | 'N'>(),
    })
    .superRefine((data, ctx) => {
      // ESD vrijgaveTijdstip
      if (data.ordertijdIndicatie === 'N') {
        if (
          lsdRadioButtonValue === LSDRadioButtonValue.FIXED_TIME &&
          compareTime(data.aflevertijd, data.vrijgaveTijdstip) < 0
        ) {
          ctx.addIssue({
            code: 'custom',
            message:
              'ESD tijdstip moet kleiner of gelijk zijn dan de andere tijdstippen (LSD).',
            path: ['vrijgaveTijdstip'],
          })
        }

        if (
          eddRadioButtonValue === EDDRadioButtonValue.FIXED_TIME &&
          data.eddWorkDaysAfterLSD === 0 &&
          compareTime(data.edd, data.vrijgaveTijdstip) < 0
        ) {
          ctx.addIssue({
            code: 'custom',
            message:
              'ESD tijdstip moet kleiner of gelijk zijn dan de andere tijdstippen (EDD).',
            path: ['vrijgaveTijdstip'],
          })
        }

        if (
          lddRadioButtonValue === LDDRadioButtonValue.FIXED_TIME &&
          compareTime(data.ldd, data.vrijgaveTijdstip) < 0
        ) {
          ctx.addIssue({
            code: 'custom',
            message:
              'ESD tijdstip moet kleiner of gelijk zijn dan de andere tijdstippen (LDD).',
            path: ['vrijgaveTijdstip'],
          })
        }
      }

      // LSD aflevertijd
      if (lddRadioButtonValue === LDDRadioButtonValue.FIXED_TIME) {
        if (
          lddRadioButtonValue === LDDRadioButtonValue.FIXED_TIME &&
          compareTime(data.ldd, data.aflevertijd) < 0
        ) {
          ctx.addIssue({
            code: 'custom',
            message: 'LSD tijdstip moet kleiner of gelijk zijn aan de LDD.',
            path: ['aflevertijd'],
          })
        }
      }

      // EDD
      if (eddRadioButtonValue === EDDRadioButtonValue.FIXED_TIME) {
        if (
          lddRadioButtonValue === LDDRadioButtonValue.FIXED_TIME &&
          compareTime(data.ldd, data.edd) < 0
        ) {
          ctx.addIssue({
            code: 'custom',
            message:
              'EDD tijdstip moet kleiner of gelijk zijn aan het LDD tijdstip.',
            path: ['edd'],
          })
        }
      }

      // LSD - lsdLeadTimeBeforeLDD
      if (
        lsdRadioButtonValue === LSDRadioButtonValue.LEAD_TIME_BEFORE_LDD &&
        lddRadioButtonValue === LDDRadioButtonValue.LEAD_TIME_AFTER_LSD
      ) {
        ctx.addIssue({
          code: 'custom',
          message:
            'Het is niet mogelijk zowel LSD doorlooptijd voor LDD als LDD doorlooptijd na LSD.',
          path: ['lsdLeadTimeBeforeLDD'],
        })
      }

      if (
        eddRadioButtonValue === EDDRadioButtonValue.FIXED_TIME &&
        data.eddWorkDaysAfterLSD > 0 &&
        (lsdRadioButtonValue === LSDRadioButtonValue.LEAD_TIME_BEFORE_LDD ||
          lddRadioButtonValue === LDDRadioButtonValue.LEAD_TIME_AFTER_LSD)
      ) {
        ctx.addIssue({
          code: 'custom',
          message:
            "Werkdagen groter dan 0 niet combineren met 'LSD: doorlooptijd vóór LDD' of 'LDD: doorlooptijd na LSD'",
          path: ['eddWorkDaysAfterLSD'],
        })
      }

      if (
        data.shipmentDaysBeforeDelivery > 0 &&
        data.ordertijdIndicatie === 'J'
      ) {
        ctx.addIssue({
          code: 'custom',
          message:
            "De 'dag'-instelling kan alleen gebruikt worden als de ESD-tijd is ingevuld, niet in combinatie met 'Ordertijd'",
          path: ['shipmentDaysBeforeDelivery'],
        })
      }

      if (
        data.shipmentDaysBeforeDelivery > 0 &&
        data.orderType.split(',').includes('34')
      ) {
        ctx.addIssue({
          code: 'custom',
          message:
            "De 'dag'-instelling kan niet gebruikt worden voor ordertype Spoedkoop",
          path: ['shipmentDaysBeforeDelivery'],
        })
      }
    })
}
